import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmptyOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { average, groupAverageBy, groupCountBy } from '@/lib/dashboardAggregations';
import { withId } from '@/pages/reports/reportHelpers';
import type { ApprovalTurnaroundRow, PendingApprovalAgingRow } from '@/types/reports';

const STATUS_SLICE_COLORS = ['#4edea3', '#ba1a1a', '#ffb951', '#0566d9'];

export default function ApprovalsPanel() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: pending = [], isLoading: loadingPending } = useQuery({
    queryKey: ['reports', 'pending-approvals-aging'],
    queryFn: async () => (await apiClient.get<PendingApprovalAgingRow[]>('/reports/pending-approvals-aging')).data,
  });
  const { data: turnaround = [], isLoading: loadingTurnaround } = useQuery({
    queryKey: ['reports', 'approval-turnaround'],
    queryFn: async () => (await apiClient.get<ApprovalTurnaroundRow[]>('/reports/approval-turnaround')).data,
  });

  const overdueCount = useMemo(() => pending.filter((r) => r.isOverdue).length, [pending]);
  const avgTurnaround = useMemo(() => average(turnaround.map((r) => r.turnaroundHours).filter((v): v is number => v != null)), [turnaround]);
  const avgPendingAge = useMemo(() => average(pending.map((r) => r.hoursPending)), [pending]);

  const byTransactionType = useMemo(() => groupCountBy(pending, (r) => r.transactionType), [pending]);
  const decisionSplit = useMemo(() => groupCountBy(turnaround, (r) => r.status), [turnaround]);
  const avgTurnaroundByType = useMemo(() => groupAverageBy(turnaround, (r) => r.transactionType, (r) => r.turnaroundHours), [turnaround]);

  const pendingColumns: DataTableColumn<ReturnType<typeof withId<PendingApprovalAgingRow>>[number]>[] = [
    { field: 'transactionType', headerName: tr('columns.transactionType'), filterable: true },
    { field: 'currentLevelNo', headerName: tr('columns.currentLevel'), align: 'right' },
    { field: 'hoursPending', headerName: tr('columns.hoursPending'), align: 'right', render: (r) => r.hoursPending.toFixed(1) },
    { field: 'isOverdue', headerName: tr('columns.status'), render: (r) => <StatusChip status={r.isOverdue ? tr('columns.overdue') : tr('columns.withinSla')} />, filterable: true },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('approvals.pendingCount')} value={pending.length} icon={<PendingActionsIcon />} accentColor={colors.approvals} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('approvals.overdueCount')} value={overdueCount} icon={<ReportProblemOutlinedIcon />} accentColor={colors.adjustments} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('approvals.avgTurnaround')} value={avgTurnaround > 0 ? `${avgTurnaround.toFixed(1)} hrs` : '—'} icon={<TimerOutlinedIcon />} accentColor={colors.approvals} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('approvals.avgPendingAge')} value={avgPendingAge > 0 ? `${avgPendingAge.toFixed(1)} hrs` : '—'} icon={<HourglassEmptyIcon />} accentColor={colors.approvals} />
        </GridItem>
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={7}>
          <ChartCard title={t('approvals.byTypeTitle')} loading={loadingPending} empty={!loadingPending && byTransactionType.length === 0}>
            <DashboardBarChart
              categories={byTransactionType.map((r) => r.key)}
              series={[{ label: t('approvals.pendingCount'), data: byTransactionType.map((r) => r.count), color: colors.approvals }]}
            />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={5}>
          <ChartCard title={t('approvals.decisionSplitTitle')} loading={loadingTurnaround} empty={!loadingTurnaround && decisionSplit.length === 0}>
            <DashboardPieChart data={decisionSplit.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))} />
          </ChartCard>
        </GridItem>
      </GridContainer>

      <ChartCard title={t('approvals.turnaroundByTypeTitle')} loading={loadingTurnaround} empty={!loadingTurnaround && avgTurnaroundByType.length === 0} height={280}>
        <DashboardBarChart
          layout="horizontal"
          categories={avgTurnaroundByType.map((r) => r.key)}
          series={[{ label: t('approvals.avgTurnaround'), data: avgTurnaroundByType.map((r) => r.average), color: colors.approvals }]}
          valueFormatter={(v) => `${(v ?? 0).toFixed(1)} hrs`}
          height={280}
        />
      </ChartCard>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('approvals.pendingTableTitle')}
        </Typography>
        <DataTable columns={pendingColumns} rows={withId(pending, (r) => r.instanceId)} loading={loadingPending} exportable={false} defaultRowsPerPage={5} rowsPerPageOptions={[5, 10, 25]} />
      </Paper>
    </Stack>
  );
}
